// File: src/features/daily-planner/components/WellBeingForm.tsx
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import Textarea from '../../../components/common/Textarea';
import Button from '../../../components/common/Button';
import { WellBeingUpdatePayload } from '../../../types';
import { format, parseISO } from 'date-fns'; // NEW: Import date-fns functions

const wellBeingSchema = z.object({
  well_being_rating: z.coerce
    .number({ invalid_type_error: "Rating must be a number." })
    .min(1, "Rating must be at least 1.")
    .max(10, "Rating cannot exceed 10."),
  notes: z.string().optional().nullable(),
});

type WellBeingFormInputs = z.infer<typeof wellBeingSchema>;

interface WellBeingFormProps {
  onSubmit: (data: WellBeingUpdatePayload) => Promise<void>;
  initialData?: { well_being_rating: number; notes?: string | null };
  isLoading: boolean;
  onCancel?: () => void;
  currentDate: string; // NEW: Prop to pass the date being edited
}

const WellBeingForm: React.FC<WellBeingFormProps> = ({ onSubmit, initialData, isLoading, onCancel, currentDate }) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    watch,
  } = useForm<WellBeingFormInputs>({
    resolver: zodResolver(wellBeingSchema),
    defaultValues: {
      well_being_rating: initialData?.well_being_rating || 5,
      notes: initialData?.notes || '',
    },
  });

  const liveRating = watch('well_being_rating');
  const [feedbackEmoji, setFeedbackEmoji] = useState('😐');
  const [placeholderText, setPlaceholderText] = useState('Any specific thoughts on your well-being today?');

  useEffect(() => {
    reset({
        well_being_rating: initialData?.well_being_rating || 5,
        notes: initialData?.notes || '',
    });
  }, [initialData, reset]);

  useEffect(() => {
    if (liveRating <= 2) {
      setFeedbackEmoji('😔');
      setPlaceholderText("What contributed to feeling low today? What's one small thing you can do for yourself?");
    } else if (liveRating <= 4) {
      setFeedbackEmoji('🙁');
      setPlaceholderText("What challenges did you face today? What's one small step to improve?");
    } else if (liveRating <= 6) {
      setFeedbackEmoji('😐');
      setPlaceholderText("What was one positive thing that happened today? What was a challenge?");
    } else if (liveRating <= 8) {
      setFeedbackEmoji('😊');
      setPlaceholderText("What made you feel good today? How can you cultivate more of this?");
    } else {
      setFeedbackEmoji('😁');
      setPlaceholderText("What made you feel so great today? How can you keep this momentum going?");
    }
  }, [liveRating]);


  const handleFormSubmit = (data: WellBeingFormInputs) => {
    const payload: WellBeingUpdatePayload = {
      well_being_rating: Number(data.well_being_rating), 
      notes: data.notes || null,
    };
    onSubmit(payload);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      {/* NEW: Display the date being edited */}
      <div className="p-3 bg-primary-lightest border border-primary-light rounded-md text-center">
        <p className="font-semibold text-primary-dark">
          Editing entry for: {format(parseISO(currentDate), 'EEEE, MMMM d, yyyy')}
        </p>
      </div>

      <div>
        <label htmlFor="wellBeingRating" className="block text-sm font-medium text-neutral-dark mb-1">
          Overall Well-being (1-10)
        </label>
        <div className="flex items-center space-x-3">
          <input
            type="range"
            id="wellBeingRating"
            min="1"
            max="10"
            step="1"
            className="w-full h-2 bg-neutral-200 rounded-lg appearance-none cursor-pointer range-lg accent-primary"
            {...register('well_being_rating', { valueAsNumber: true })}
          />
          <div className="flex-shrink-0 text-xl font-semibold text-primary min-w-[3rem] text-center">
            {liveRating} {feedbackEmoji}
          </div>
        </div>
        {errors.well_being_rating && <p className="mt-1 text-xs text-red-600">{errors.well_being_rating.message}</p>}
      </div>

      <Textarea
        label="Notes (Optional)"
        id="wellBeingNotes"
        error={errors.notes?.message}
        {...register('notes')}
        rows={3}
        placeholder={placeholderText}
      />
      <div className="flex justify-end space-x-2 pt-2">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
        )}
        <Button type="submit" variant="primary" isLoading={isLoading} disabled={isLoading || (!isDirty && !!initialData)}>
          {initialData ? 'Update Well-being' : 'Save Well-being'}
        </Button>
      </div>
    </form>
  );
};

export default WellBeingForm;